import { useEffect, useState } from 'react';
import { supabase } from '../../lib/supabase/client';
import { zaehleOffeneVorschlaege } from '../../lib/ki/triage';

/**
 * Daten der Seitenleisten: aktive Module (portal_modules) und Zähler-Badges.
 * Gemeinsame Quelle für die klassische Sidebar und die Immobilien-Sidebar.
 */
export interface SidebarDaten {
  activeModules: string[];
  loading: boolean;
  /** Fällige (≤ 3 Tage) unbezahlte Rechnungen, ohne „klaerung“ und Archiv */
  rechnungenBadge: number;
  /** Ungelesene eingehende E-Mails aktiver Postfächer */
  emailBadge: number;
  /** Offene KI-Vorschläge */
  vorschlaegeBadge: number;
}

const STANDARD_MODULE = ['dashboard', 'objekte', 'rechnungen', 'eigentuemer', 'mieter', 'team', 'dokumente', 'profile'];

async function ladeRechnungenBadge(): Promise<number> {
  const heute = new Date();
  heute.setHours(0, 0, 0, 0);
  const in3Tagen = new Date(heute);
  in3Tagen.setDate(in3Tagen.getDate() + 3);
  // 'klaerung' zählt NICHT mit (bewusst geparkte Belege hielten das Badge sonst
  // dauerhaft auf 9+) — gleiche Logik wie die Überfällig-Karte in InvoicesView.
  const { count } = await supabase
    .from('rechnungen')
    .select('id', { count: 'exact', head: true })
    .neq('status', 'bezahlt')
    .neq('status', 'klaerung')
    .is('archiviert_am', null)
    .not('faelligkeitsdatum', 'is', null)
    .lte('faelligkeitsdatum', in3Tagen.toISOString().split('T')[0]);
  return count || 0;
}

async function ladeEmailBadge(): Promise<number> {
  const { data: konten } = await supabase
    .from('email_konten')
    .select('id')
    .eq('aktiv', true);
  if (!konten || konten.length === 0) return 0;
  const kontoIds = konten.map((k: { id: string }) => k.id);
  const { count } = await supabase
    .from('emails')
    .select('id', { count: 'exact', head: true })
    .in('konto_id', kontoIds)
    .eq('gelesen', false)
    .eq('richtung', 'eingang')
    .is('geloescht_am', null);
  return count || 0;
}

async function ladeAktiveModule(): Promise<string[]> {
  const { data, error } = await supabase
    .from('portal_modules')
    .select('id')
    .eq('is_active', true);
  if (error) throw error;
  return data ? data.map((m: { id: string }) => m.id) : STANDARD_MODULE;
}

export function useSidebarDaten(): SidebarDaten {
  const [activeModules, setActiveModules] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [rechnungenBadge, setRechnungenBadge] = useState(0);
  const [emailBadge, setEmailBadge] = useState(0);
  const [vorschlaegeBadge, setVorschlaegeBadge] = useState(0);

  useEffect(() => {
    let aktiv = true;
    ladeAktiveModule()
      .then(m => { if (aktiv) setActiveModules(m); })
      .catch(err => {
        console.error('Fehler beim Laden der Sidebar-Konfiguration:', err);
        if (aktiv) setActiveModules(STANDARD_MODULE);
      })
      .finally(() => { if (aktiv) setLoading(false); });
    ladeRechnungenBadge()
      .then(n => { if (aktiv) setRechnungenBadge(n); })
      .catch(err => console.error('Badge-Count fehlgeschlagen:', err));
    ladeEmailBadge()
      .then(n => { if (aktiv) setEmailBadge(n); })
      .catch(err => console.error('Email-Badge fehlgeschlagen:', err));
    zaehleOffeneVorschlaege()
      .then(n => { if (aktiv) setVorschlaegeBadge(n); })
      .catch(() => {});
    return () => { aktiv = false; };
  }, []);

  return { activeModules, loading, rechnungenBadge, emailBadge, vorschlaegeBadge };
}
